import type { RefObject } from 'react';

import { MAX_MESSAGE_LENGTH } from '@/common/constants';
import {
  getEffectiveReasoning,
  type DevModelName,
  type DevProvider,
  type DevReasoningLevel,
} from '@/components/providers/dev-tools-provider';
import type { Platform } from '@/components/providers/platform-provider';
import { AnalyticsChatTransport } from '@/lib/assistant-cloud-local/analytics-chat-transport';
import { pushPendingTiming } from '@/lib/assistant-cloud-local/stores/assistant-cloud-store';
import { markServerIdentityEstablished } from '@/lib/assistant-cloud-server';
import { clearInitialComposerText, getInitialComposerText } from '@/lib/composer-text-tracking';
import { CHAT_ANALYTICS_EVENTS } from '@/lib/constants/post-message-types';
import { consumeDictationTranscript, isDictatedSend } from '@/lib/dictation/dictation-telemetry';
import {
  encodeHitlCredentials,
  HITL_CREDENTIALS_HEADER,
  turnCarriesApprovalResponseFor,
} from '@/lib/hitl/credential-header';
import {
  consumeInterruptionElapsedMs,
  getResumableRecord,
  RESUMABLE_STREAM_SET_EVENT,
} from '@/lib/hitl/resumable-storage';
import { useSyncStore } from '@/lib/sync/store';
import { useThreadStatusStore } from '@/lib/thread-status/store';
import { fetchWithTimeout } from '@/lib/utils/api-client';
import { hasConversationStarted, markConversationStarted } from '@/lib/utils/conversation-dedup';
import { getPendingDeletions } from '@/lib/utils/deletion-queue';
import { setServerMessageId } from '@/lib/utils/message';
import { resolveResourceId } from '@/lib/utils/resource-id';
import { safeRandomUUID } from '@/lib/utils/safe-uuid';
import { getThreadUuid } from '@/lib/utils/thread';

type TrackEvent = (params: { event: string; properties?: Record<string, unknown> }) => void;

type PostHogClient = { get_distinct_id(): string };

interface DevToolsSnapshot {
  showDevTools: boolean;
  selectedModel: DevModelName;
  provider: DevProvider;
  reasoning: DevReasoningLevel;
}

/** Live HITL credential bindings — set by the runtime provider once the tenant module loads. */
export interface HitlTransportBindings {
  getCredentials: () => Promise<Record<string, string>>;
  credentialToolNames: ReadonlySet<string>;
}

export interface CreateChatTransportOptions {
  chatApiBaseUrl: string;
  tenantName: string;
  // Live refs read at request time so the transport sees the latest values
  // without being re-created. Stable values (URL pieces) are captured directly.
  localeRef: { readonly current: string };
  platformRef: { readonly current: Platform | undefined };
  isDebugModeRef: { readonly current: boolean };
  posthogRef: { readonly current: PostHogClient | null | undefined };
  trackEventRef: { readonly current: TrackEvent };
  currentThreadIdRef: RefObject<string | null>;
  erroredThreadIdsRef: RefObject<Set<string>>;
  pendingResponsesRef: RefObject<Map<string, number>>;
  streamingStartedRef: RefObject<Set<string>>;
  retriedResponsesRef: RefObject<Set<string>>;
  lastMessageCountRef: RefObject<Map<string, number>>;
  devToolsRef: { readonly current: DevToolsSnapshot };
  /** Null until the tenant's HITL module (if any) declares getCredentials. */
  hitlRef?: { readonly current: HitlTransportBindings | null };
  /** True when the tenant's hitlPages gate suppressed HITL on this page (forwarded to the backend). */
  hitlSuppressed?: boolean;
  /** True for persistence:'server' tenants — the chat POST then carries only the trailing turn. */
  serverPersistence?: boolean;
  /** Resumable-streaming options for opted-in tenants. */
  resumable?: import('@assistant-ui/react-ai-sdk').AssistantChatResumableOptions;
}

// Builds the chat transport. All mutable inputs flow in as refs so the
// transport reads the latest values inside its option callbacks (which run at
// request time, not during render). Defined in module scope so React-hooks
// lint rules don't analyze ref reads here as "in render".
export function createChatTransport({
  chatApiBaseUrl,
  tenantName,
  localeRef,
  platformRef,
  isDebugModeRef,
  posthogRef,
  trackEventRef,
  currentThreadIdRef,
  erroredThreadIdsRef,
  pendingResponsesRef,
  streamingStartedRef,
  retriedResponsesRef,
  lastMessageCountRef,
  devToolsRef,
  hitlRef,
  hitlSuppressed,
  serverPersistence,
  resumable,
}: CreateChatTransportOptions): AnalyticsChatTransport {
  const transport: AnalyticsChatTransport = new AnalyticsChatTransport({
    api: `${chatApiBaseUrl}/${tenantName}/chat`,
    resumable,
    credentialed: serverPersistence,
    onStreamStart: (event, properties) => {
      const threadId = (properties as { threadId?: string }).threadId;
      trackEventRef.current({
        event,
        properties: {
          ...properties,
          isRetry: !!threadId && retriedResponsesRef.current.has(threadId),
        },
      });
    },
    onStreamingStarted: threadId => streamingStartedRef.current.add(threadId),
    onStreamFinish: ({ threadId, durationMs, stepTimings }) => {
      // Stash both stream duration and per-step timings; createMessage will
      // consume them when the assistant message is persisted (which happens
      // right after this callback runs). The duration backdates `created_at`,
      // and the step timings populate `step_timings` so the activity-trail can
      // render a per-step breakdown post-reload.
      pushPendingTiming({ threadId, durationMs, timings: stepTimings });
      // Fires on completion, error, AND cancel — the only seam that sees a stream severed by
      // switching away from the thread, where no aui runEnd event is emitted (belt-and-braces
      // next to the runEnd feed; markRunEnd is idempotent). Gated exactly like the feed's writes
      // so non-server-persistence tenants get zero thread-status dots (byte-identical sidebar).
      if (serverPersistence) useThreadStatusStore.getState().markRunEnd(threadId);
    },
    fetch: async (url, options) => {
      const requestUrl = url.toString();
      const response = await fetchWithTimeout({
        url: url.toString(),
        options: {
          ...options,
          headers: {
            ...((options?.headers as Record<string, string>) ?? {}),
            Connection: 'keep-alive',
          },
        },
        timeout: 2 * 60 * 1000,
      });
      // Bodyless statuses (e.g. the resume route's 204 "nothing to replay"): hand back a CLEAN
      // bodyless Response — retry/timeout wrappers can leave a non-null empty body, and the
      // resumable fetch wrapper would then construct `new Response(body, {status: 204})`, which
      // throws. A 204 on the resume GET also means the stream is gone — drop the stored record
      // so later boots stop re-asking.
      if (response.status === 204 || response.status === 205 || response.status === 304) {
        if (resumable && requestUrl.includes('/chat/resume/')) {
          resumable.storage.clear();
        }
        return new Response(null, { status: response.status, headers: response.headers });
      }
      // A resume GET that returns bytes (200) is a real re-attach — fire RESPONSE_RESUMED (the
      // resume half of the interrupt→resume funnel, parallel to RESPONSE_INTERRUPTED). Gated to
      // resumable tenants by construction (`resumable` is set only for them).
      if (resumable && response.ok && requestUrl.includes('/chat/resume/')) {
        const record = getResumableRecord(tenantName);
        const threadId = record?.threadUuid;
        const msSinceInterruption = threadId
          ? consumeInterruptionElapsedMs({ tenantName, threadUuid: threadId })
          : undefined;
        trackEventRef.current({
          event: CHAT_ANALYTICS_EVENTS.RESPONSE_RESUMED,
          properties: {
            threadId,
            via: record?.source ?? 'header',
            ...(msSinceInterruption !== undefined && { msSinceInterruption }),
          },
        });
      }
      const messageId = response.headers.get('X-Message-Id');
      if (currentThreadIdRef.current && messageId) {
        setServerMessageId(currentThreadIdRef.current, messageId);
      }
      // A successful chat POST minted (or refreshed) the HttpOnly identity cookie — record the
      // client-readable marker that authorizes AssistantCloudServer to start reading history.
      // Only server-persistence tenants mint the cookie / read via that marker; skip the write
      // for everyone else so no stray localStorage marker appears for non-opted tenants.
      if (serverPersistence && response.ok && options?.method === 'POST') {
        markServerIdentityEstablished({ tenant: tenantName });
      }
      return response;
    },
    prepareSendMessagesRequest: async params => {
      const distinctId = resolveResourceId(posthogRef.current?.get_distinct_id(), {
        serverPersistence,
      });
      const threadId = params.id ? getThreadUuid(params.id) : undefined;
      const deletedMessageIds = params.id ? await getPendingDeletions(params.id) : [];

      currentThreadIdRef.current = threadId ?? null;

      // Resumable tenants: point the persisted current-thread at the thread with the in-flight
      // run as soon as it starts (thread restore otherwise only learns about it at run END, so a
      // mid-stream reload would boot into a blank thread with nothing to resume).
      // Mint the resumable stream id client-side and send it as a request header so the backend uses
      // it (and echoes it back). Announce it to sibling tabs NOW (at send): the receiver doesn't
      // remount immediately — it polls the discovery endpoint until the thread exists in the DB
      // (which the backend persists before the first token) and only then adopts it. This lets a
      // sibling show the thread + question as soon as the run is created, without waiting for the
      // first token (important for tool-calling turns).
      let clientStreamId: string | undefined;
      if (resumable && threadId) {
        useSyncStore.getState().setThreadId(threadId);
        clientStreamId = safeRandomUUID();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent(RESUMABLE_STREAM_SET_EVENT, {
              detail: { streamId: clientStreamId, threadUuid: threadId },
            }),
          );
        }
      }

      const userMessages = params.messages.filter(m => m.role === 'user');
      const messageCount = userMessages.length;
      const isFirstMessage = messageCount === 1;

      // Use params.id (raw thread ID) as the key for tracking, since threadId (UUID) may change
      // between first message and retry when local IDs are involved
      const trackingKey = params.id || 'new-thread';

      const previousMessageCount = lastMessageCountRef.current.get(trackingKey);
      const isRetry = previousMessageCount !== undefined && messageCount === previousMessageCount;

      lastMessageCountRef.current.set(trackingKey, messageCount);

      if (threadId && erroredThreadIdsRef.current.has(threadId)) {
        erroredThreadIdsRef.current.delete(threadId);
      }

      // Only fire user_message_sent for NEW messages, not retries
      // Retries are tracked via response_retry_clicked or response_auto_retried
      if (!isRetry) {
        if (isFirstMessage && (!threadId || !hasConversationStarted(threadId))) {
          trackEventRef.current({
            event: CHAT_ANALYTICS_EVENTS.CONVERSATION_STARTED,
            properties: { threadId },
          });
          if (threadId) {
            markConversationStarted(threadId);
          }
        }

        const initialComposerText = getInitialComposerText();
        const lastUserMessage = userMessages[userMessages.length - 1];
        const textPart = lastUserMessage?.parts?.find(
          (p): p is { type: 'text'; text: string } => p.type === 'text',
        );
        const submittedText = textPart?.text;

        const dictatedTranscript = consumeDictationTranscript();
        const dictated = isDictatedSend({ submittedText, transcript: dictatedTranscript });

        const userMessageProperties = {
          threadId,
          messageCount,
          submittedText,
          ...(initialComposerText && {
            initialComposerText,
            submittedAsIs: initialComposerText === submittedText,
          }),
          ...(dictated && { dictated: true, dictatedChars: dictatedTranscript.length }),
        };
        trackEventRef.current({
          event: CHAT_ANALYTICS_EVENTS.USER_MESSAGE_SENT,
          properties: userMessageProperties,
        });

        clearInitialComposerText();
      }

      const requestStartTime = Date.now();
      if (threadId) {
        pendingResponsesRef.current.set(threadId, requestStartTime);
        // Read by the lifecycle events; cleared on non-retry so a previous
        // run's flag can't leak onto this response.
        if (isRetry) retriedResponsesRef.current.add(threadId);
        else retriedResponsesRef.current.delete(threadId);
      }
      transport.setRequestContext(threadId ?? null, requestStartTime);

      // Server-persistence tenants: Mastra memory is the history — the docs warn that sending
      // prior messages alongside memory causes ordering/duplicate bugs. Send only the trailing
      // turn (last user message onward, which keeps HITL resolved-tool / approval-response
      // assistant parts that follow it); other tenants keep the last-10 window.
      const lastUserIndex = serverPersistence
        ? params.messages.findLastIndex(m => m.role === 'user')
        : -1;
      const turnMessages =
        serverPersistence && lastUserIndex >= 0
          ? params.messages.slice(lastUserIndex)
          : params.messages.slice(-10);
      const recentMessages = turnMessages.map(message => {
        if (message.role !== 'user') return message;
        return {
          ...message,
          parts: message.parts.map(part =>
            part.type === 'text' ? { ...part, text: part.text.slice(0, MAX_MESSAGE_LENGTH) } : part,
          ),
        };
      });

      const platform = platformRef.current;
      const isDebugMode = isDebugModeRef.current;

      // Server-approval credentials: harvested fresh and attached as a header ONLY on the turn that
      // carries the approval response for a credential-declaring tool. Never in the body.
      let credentialHeaders: Record<string, string> | undefined;
      const hitl = hitlRef?.current;
      if (
        hitl &&
        turnCarriesApprovalResponseFor({
          messages: params.messages,
          toolNames: hitl.credentialToolNames,
        })
      ) {
        const credentials = await hitl.getCredentials();
        if (Object.keys(credentials).length > 0) {
          credentialHeaders = { [HITL_CREDENTIALS_HEADER]: encodeHitlCredentials(credentials) };
        }
      }

      const headers = {
        ...credentialHeaders,
        ...(clientStreamId && { 'x-resumable-stream-id': clientStreamId }),
      };

      return {
        ...params,
        ...(Object.keys(headers).length > 0 && { headers }),
        body: {
          memory: {
            thread: threadId,
            resource: distinctId,
          },
          messages: recentMessages,
          deletedMessageIds,
          languageCode: localeRef.current,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          ...(platform && { platform }),
          ...(hitlSuppressed && { hitlSuppressed: true }),
          ...(isDebugMode && { debug: isDebugMode }),
          ...(devToolsRef.current.showDevTools && {
            reasoning: getEffectiveReasoning({
              model: devToolsRef.current.selectedModel,
              provider: devToolsRef.current.provider,
              reasoning: devToolsRef.current.reasoning,
            }),
            model: devToolsRef.current.selectedModel,
            provider: devToolsRef.current.provider,
          }),
        },
      };
    },
  });

  return transport;
}
